import { runInAction } from "mobx";

import { ViewClient } from "@formant/core/client/ViewClient";
import { defined } from "@formant/core/common/defined";
import { matchesTags } from "@formant/core/common/matchesTags";
import { PromiseLimit } from "@formant/core/common/PromiseLimit";
import { deserializeHash } from "@formant/core/common/serializeHash";
import { tagSetsToTags } from "@formant/core/common/tagSetsToTags";
import { DefinedEntity } from "@formant/core/model/DefinedEntity";
import { IGroup } from "@formant/core/model/IGroup";
import { ITags } from "@formant/core/model/ITags";
import { IView } from "@formant/core/model/IView";
import { Uuid } from "@formant/core/model/Uuid";

import { namedIndexedComparator } from "../utils/indexedItemUtils";
import { reorderIndices } from "../utils/indexedItemUtils/reorderIndices";
import { IAuthStore } from "./IAuthStore";
import { IDeviceDetailsStore } from "./IDeviceDetailsStore";
import { IViewStore } from "./IViewStore";
import { StoreCache } from "./StoreCache";
import {
    AsyncFuture,
    EitherFuture,
    IBaseAsyncOptions,
    IBaseFutureOptions,
    IBaseSyncOptions,
    SyncFuture,
} from "./StoreFuture.types";

const reorderingLimiter = new PromiseLimit({
    name: "ViewStore-reorderingLimiter",
    concurrency: 10,
});

export class ViewStore implements IViewStore {
    private cache = new StoreCache<Uuid, DefinedEntity<IView> | false>();
    private listCache = new StoreCache<void, DefinedEntity<IView>[]>();

    constructor(
        private viewClient: ViewClient,
        private authStore: IAuthStore,
        private deviceDetailsStore: IDeviceDetailsStore,
    ) {}

    /** Get One */
    get(
        id: Uuid,
        options?: IBaseSyncOptions,
    ): SyncFuture<DefinedEntity<IView> | false>;
    get(
        id: Uuid,
        options: IBaseAsyncOptions,
    ): AsyncFuture<DefinedEntity<IView> | false>;
    get(
        id: Uuid,
        options: IBaseFutureOptions = {},
    ): EitherFuture<DefinedEntity<IView> | false> {
        const { async = false, force = false } = options;
        if (force) {
            this.cache.expire(id);
        }
        return !async
            ? this.cache.get(id, () => this.fetchOne(id))
            : this.cache.getAsync(id, () => this.fetchOne(id));
    }

    /** Get List */
    list(options?: IBaseSyncOptions): SyncFuture<DefinedEntity<IView>[]>;
    list(options: IBaseAsyncOptions): AsyncFuture<DefinedEntity<IView>[]>;
    list(
        options: IBaseFutureOptions = {},
    ): EitherFuture<DefinedEntity<IView>[]> {
        const { async = false, force = false } = options;
        if (force) {
            this.listCache.expire();
        }
        return !async
            ? this.listCache.get(undefined, () => this.fetchList())
            : this.listCache.getAsync(undefined, () => this.fetchList());
    }

    listMatching(tags: ITags): SyncFuture<DefinedEntity<IView>[]> {
        const all = this.list();
        if (!all) {
            return undefined;
        }
        // return all views that have at least one tag in common with a device
        // or have no tags at all.
        return all.filter(_ =>
            matchesTags(
                tags,
                // for backwards compatability, if device filter is not set
                // use view tags
                _.deviceFilter?.tags
                    ? tagSetsToTags(_.deviceFilter.tags)
                    : _.tags,
                {
                    logic: "or",
                    ignoreReservedTags: true,
                },
            ),
        );
    }

    listMatchingSingleDevice(tags: ITags): SyncFuture<DefinedEntity<IView>[]> {
        return (this.listMatching(tags) || []).filter(
            _ => !_.smartFleetId || !_.groupFilter,
        );
    }

    listSmartFleet(group: IGroup): SyncFuture<DefinedEntity<IView>[]> {
        const all = this.list();
        if (!all) {
            return undefined;
        }
        const views = all.filter((_: IView) => {
            const groupTags = _?.groupFilter?.tags;
            const t = groupTags ? tagSetsToTags(groupTags) : undefined;

            return (
                _.smartFleetId === group.id ||
                (t &&
                    t[group.tagKey] &&
                    t[group.tagKey].includes(group.tagValue))
            );
        });
        return views;
    }

    async fetchListSmartFleet(group: IGroup): Promise<DefinedEntity<IView>[]> {
        const all = await this.list({ async: true });
        if (!all) {
            return [];
        }
        const views = all.filter((_: IView) => {
            const groupTags = _?.groupFilter?.tags;
            const t = groupTags ? tagSetsToTags(groupTags) : undefined;

            return (
                _.smartFleetId === group.id ||
                (t &&
                    t[group.tagKey] &&
                    (t[group.tagKey].includes(group.tagValue) ||
                        t[group.tagKey].includes("*"))) // Include the condition for '*'
            );
        });
        return views;
    }

    /** Mutations */
    async save(view: IView): Promise<DefinedEntity<IView> | Partial<IView>> {
        if (view.id === undefined) {
            return this.create(view);
        } else {
            return this.update(view.id, view);
        }
    }

    async create(view: IView): Promise<DefinedEntity<IView>> {
        const created = await this.viewClient.create(
            await this.authStore.token(),
            view,
        );
        this.cache.set(defined(created.id), created);
        this.listCache.clear();
        this.deviceDetailsStore.clear();
        return created;
    }

    async update(id: Uuid, view: Partial<IView>): Promise<Partial<IView>> {
        const updated = await this.viewClient.update(
            await this.authStore.token(),
            id,
            view,
        );
        runInAction(() => {
            this.cache.set(id, updated);
            this.listCache.clear();
            this.deviceDetailsStore.clear();
        });
        return view;
    }

    async updateSimple(
        id: Uuid,
        view: Partial<IView>,
    ): Promise<Partial<IView>> {
        const updated = await this.viewClient.update(
            await this.authStore.token(),
            id,
            view,
        );

        const previous = await this.list({ async: true });

        runInAction(() => {
            this.cache.set(id, updated);
            if (previous) {
                this.listCache.set(
                    undefined,
                    previous.map(_ => (_.id === updated.id ? updated : _)),
                );
            }
        });
        return view;
    }

    async createSimple(view: IView): Promise<DefinedEntity<IView>> {
        const created = await this.viewClient.create(
            await this.authStore.token(),
            view,
        );
        const previous = await this.list({ async: true });

        runInAction(() => {
            this.cache.set(defined(created.id), created);
            this.deviceDetailsStore.clear();
            if (previous) {
                this.listCache.set(undefined, [
                    created,
                    ...previous.filter(_ => _.id !== created.id),
                ]);
            }
        });
        return created;
    }

    async delete(id: Uuid): Promise<void> {
        this.cache.set(id, false);
        await this.viewClient.delete(await this.authStore.token(), id);
        this.listCache.clear();
        this.deviceDetailsStore.clear();
    }

    async getCurrentView(): AsyncFuture<DefinedEntity<IView> | false> {
        const currentURL = window.location.href;
        const hashIndex = currentURL.indexOf("#");
        const hash = currentURL.slice(hashIndex);
        const context = deserializeHash(hash) as { viewId: Uuid };
        return await this.get(context.viewId, { async: true });
    }

    async reorder(
        moveId: Uuid,
        placement: "before" | "after",
        dropId: Uuid,
    ): Promise<void> {
        const previous = await this.list({ async: true });

        const updates = reorderIndices(
            previous,
            namedIndexedComparator,
            moveId,
            placement,
            dropId,
        );

        // Optimistic projection
        const updateMap = new Map(updates);
        const projection = previous.map(it => {
            return updateMap.has(it.id)
                ? { ...it, ...updateMap.get(it.id) }
                : it;
        });
        runInAction(() => {
            this.listCache.set(undefined, projection);
            projection.forEach(it => this.cache.set(defined(it.id), it));
        });

        try {
            /* FIXME should be a single backend call and/or use fractional positioning. */
            await Promise.all(
                updates.map(([id, update]) =>
                    reorderingLimiter.run(async () => {
                        await this.viewClient.update(
                            await this.authStore.token(),
                            id,
                            update,
                        );
                    }),
                ),
            );
        } catch (err) {
            runInAction(() => {
                this.listCache.set(undefined, previous);
                previous.forEach(it => this.cache.set(defined(it.id), it));
            });
            throw err;
        }
    }

    /** Fetchers */
    /* All fetchers should *always* be private. Use get() and list() options. */
    private async fetchOne(id: Uuid): Promise<DefinedEntity<IView> | false> {
        const authToken = await this.authStore.token();
        return (await this.viewClient.get(authToken, id)) ?? false;
    }

    public async fetchList(): Promise<DefinedEntity<IView>[]> {
        const authToken = await this.authStore.token();
        const all = await this.viewClient.list(authToken);
        const visible = all.filter(_ => _.layoutType !== "analytics");
        runInAction(() => {
            visible.forEach(_ => this.cache.set(defined(_.id), _));
        });
        return visible;
    }
}
